const MAX_VALUE_CHARS = 1200;

/** Read first matching key from each device's cached API payload (`rawPayload`). */
export const readDevicePayloadField = (device, keys) => {
  const payload = device?.rawPayload;
  if (!payload || typeof payload !== "object") return undefined;
  const list = Array.isArray(keys) ? keys : [keys];
  for (let i = 0; i < list.length; i += 1) {
    const key = list[i];
    if (Object.prototype.hasOwnProperty.call(payload, key)) return payload[key];
  }
  return undefined;
};

export const formatNullableBoolean = (value) => {
  if (value === null || value === undefined) return "No Data";
  return value ? "True" : "False";
};

/** Charging / plugged state from device_info (supports camelCase and snake_case keys). */
export const readDeviceIsPlugged = (device) => {
  const info = device?.deviceInfo;
  if (!info || typeof info !== "object") return null;

  const raw = info.isPlugged ?? info.is_pluged ?? info.is_plugged;
  if (raw === true || raw === false) return raw;
  if (raw === "true") return true;
  if (raw === "false") return false;
  return null;
};

export const getDeviceChargingFilterValue = (device) => {
  const isPlugged = readDeviceIsPlugged(device);
  if (isPlugged === true) return "true";
  if (isPlugged === false) return "false";
  return "no data";
};

/** True when the device payload explicitly marks allowed as false. */
export const isDeviceNotAllowed = (device) =>
  readDevicePayloadField(device, ["allowed"]) === false;

const KIOSK_FOREGROUND_ACTIVITY_EXACT = "com.catchretail.android";
const KIOSK_FOREGROUND_ACTIVITY_SUFFIX = ".presentation.activities.MainActivity";

const readDeviceInfoBoolean = (info, keys) => {
  const list = Array.isArray(keys) ? keys : [keys];
  for (let i = 0; i < list.length; i += 1) {
    const key = list[i];
    if (!Object.prototype.hasOwnProperty.call(info, key)) continue;
    const raw = info[key];
    if (raw === true || raw === "true") return true;
    if (raw === false || raw === "false") return false;
  }
  return null;
};

const isKioskForegroundActivity = (activity) => {
  if (activity == null || activity === "") return false;
  const trimmed = String(activity).trim();
  return (
    trimmed === KIOSK_FOREGROUND_ACTIVITY_EXACT ||
    trimmed.endsWith(KIOSK_FOREGROUND_ACTIVITY_SUFFIX)
  );
};

/** True when device_info indicates kiosk mode with the Catch retail app in foreground. */
export const isDeviceInKiosk = (device) => {
  const info = device?.deviceInfo;
  if (!info || typeof info !== "object") return false;

  const kioskMode = readDeviceInfoBoolean(info, ["kioskMode", "kiosk_mode"]);
  const kioskLocked = readDeviceInfoBoolean(info, ["kioskLocked", "kiosk_locked"]);
  const foregroundActivity = info.foregroundActivity ?? info.foreground_activity;

  if (kioskMode !== true || kioskLocked !== true) return false;
  return isKioskForegroundActivity(foregroundActivity);
};

export const getDeviceInKioskFilterValue = (device) =>
  isDeviceInKiosk(device) ? "true" : "false";

export const flattenDevices = (source) => {
  if (!source || typeof source !== "object") return [];
  const flat = [];
  Object.entries(source).forEach(([retailerName, branches]) => {
    Object.entries(branches || {}).forEach(([branchName, devices]) => {
      Object.entries(devices || {}).forEach(([deviceId, payload]) => {
        flat.push({
          retailerName,
          branchName,
          deviceId,
          isConnected: Boolean(payload?.is_connected),
          deviceInfo: payload?.device_info || {},
          rawPayload: payload || {},
          retailerId: payload?.retailer_id ?? payload?.retailer?.id ?? null,
          branchId: payload?.branch_id ?? payload?.branch?.id ?? null,
        });
      });
    });
  });
  return flat;
};

export const formatTime = (timestamp) => {
  if (!timestamp) return "No Data";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "No Data";
  return date.toLocaleString();
};

export const normalizeTimestampMs = (timestamp) => {
  if (timestamp === null || timestamp === undefined || timestamp === "") return null;
  const numeric = Number(timestamp);
  if (Number.isFinite(numeric)) {
    const absNumeric = Math.abs(numeric);
    return absNumeric < 1e11 ? numeric * 1000 : numeric;
  }
  const parsedMs = new Date(timestamp).getTime();
  return Number.isFinite(parsedMs) ? parsedMs : null;
};

export const cleanSsid = (ssid) => {
  const value = String(ssid || "").trim();
  if (!value || value.toLowerCase() === "null") return "No Data";
  return value.replace(/^"+|"+$/g, "");
};

/** Best-effort IP from a cached device/API payload (top-level or nested under device_info). */
export const readDeviceIpFromPayload = (payload) => {
  if (!payload || typeof payload !== "object") return "";
  const info = payload.device_info;
  const candidates = [
    payload.device_ip,
    payload.deviceIp,
    payload.ip_address,
    payload.ipAddress,
    payload.ip,
    info && typeof info === "object" ? info.ip : undefined,
    info && typeof info === "object" ? info.ip_address : undefined,
    info && typeof info === "object" ? info.ipAddress : undefined,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const raw = candidates[i];
    if (raw === null || raw === undefined) continue;
    const s = String(raw).trim();
    if (!s || s.toLowerCase() === "null" || s.toLowerCase() === "undefined") continue;
    return s;
  }
  return "";
};

export const deviceHasIpOnRecord = (device) => Boolean(readDeviceIpFromPayload(device?.rawPayload));

export const getStatus = (isConnected) => {
  if (isConnected) {
    return {
      label: "Connected",
      classes: "bg-green-100 text-green-700",
    };
  }
  return {
    label: "Disconnected",
    classes: "bg-error-container text-error",
  };
};

export const getBatteryIcon = (batteryLevel) => {
  if (!batteryLevel) {
    return "battery_unknown";
  }
  const battery = Number(batteryLevel);
  if (!Number.isFinite(battery)) return "battery_unknown";
  if (battery >= 90) return "battery_full";
  if (battery >= 70) return "battery_6_bar";
  if (battery >= 50) return "battery_5_bar";
  if (battery >= 30) return "battery_3_bar";
  if (battery >= 15) return "battery_2_bar";
  return "battery_0_bar";
};

export const formatBatteryLevelText = (batteryLevel) => {
  const n = Number(batteryLevel);
  return Number.isFinite(n) ? `${n}%` : "No Data";
};

export const truncateText = (text) => {
  const s = String(text ?? "");
  if (s.length <= MAX_VALUE_CHARS) return s;
  return `${s.slice(0, MAX_VALUE_CHARS)}...(truncated)`;
};

export const LOCATION_DEVICE_INFO_KEYS = [
  "locationLatitude",
  "locationLongitude",
];

const readLocationCoordinates = (source) => {
  if (!source || typeof source !== "object") return null;
  const lat = source.locationLatitude;
  const lon = source.locationLongitude;
  if (lat == null && lon == null) return null;
  const location = {};
  if (lat != null) location.locationLatitude = lat;
  if (lon != null) location.locationLongitude = lon;
  return location;
};

export const extractLocationFromDeviceInfo = (deviceInfo) => readLocationCoordinates(deviceInfo);

export const LOCATION_HISTORY_MAX_RECORDS = 50;

export const normalizeLocationHistory = (rawHistory) => {
  if (rawHistory == null) return [];
  let parsed = rawHistory;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return [];
    }
  }
  if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
    return Object.keys(parsed).length ? [parsed] : [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed.filter((entry) => entry && typeof entry === "object");
};

/**
 * Build display rows for MDM location history (newest first).
 * Falls back to the current device_info location when history is empty.
 */
export const buildMdmLocationHistoryEntries = (locationHistory, deviceInfo = null) => {
  const history = normalizeLocationHistory(locationHistory);
  if (history.length) {
    return history
      .map((entry, index) => {
        const latitude = entry.locationLatitude ?? entry.latitude ?? null;
        const longitude = entry.locationLongitude ?? entry.longitude ?? null;
        const updatedAt = entry.updated_at ?? entry.updatedAt ?? null;
        const agentCheckIn = entry.timestamp ?? null;
        return {
          historyId: `hist-${index}-${updatedAt || agentCheckIn || index}`,
          latitude,
          longitude,
          updatedAt: updatedAt || agentCheckIn,
          agentCheckIn,
          hasData: latitude != null || longitude != null || updatedAt || agentCheckIn,
        };
      })
      .reverse();
  }

  const parsedLocation = extractLocationFromDeviceInfo(deviceInfo);
  if (!parsedLocation) return [];
  const agentCheckIn = deviceInfo?.timestamp ?? null;
  return [
    {
      historyId: "current",
      latitude: parsedLocation.locationLatitude ?? null,
      longitude: parsedLocation.locationLongitude ?? null,
      updatedAt: agentCheckIn,
      agentCheckIn,
      hasData: true,
    },
  ];
};

export const getLocationLogPayload = (logResult) => {
  if (!logResult) return null;
  if (logResult?.location_log && typeof logResult.location_log === "object") {
    return logResult.location_log;
  }
  const payload = logResult?.data ?? logResult;
  if (!payload || typeof payload !== "object") return null;
  if (payload?.location_log && typeof payload.location_log === "object") {
    return payload.location_log;
  }
  const nested = payload?.data;
  if (nested && typeof nested === "object") return nested;
  return payload;
};

export const getFieldScreenshotPayload = (screenshotResult) => {
  if (!screenshotResult) return null;
  if (screenshotResult?.field_screenshot && typeof screenshotResult.field_screenshot === "object") {
    return screenshotResult.field_screenshot;
  }
  const payload = screenshotResult?.data ?? screenshotResult;
  if (!payload || typeof payload !== "object") return null;
  if (payload?.field_screenshot && typeof payload.field_screenshot === "object") {
    return payload.field_screenshot;
  }
  if (payload?.url || payload?.screenshot_url || payload?.image_url) {
    return payload;
  }
  const nested = payload?.data;
  if (nested && typeof nested === "object") return nested;
  return null;
};

export const buildFieldScreenshotDataFromScreenshots = (screenshots = []) => {
  if (!Array.isArray(screenshots) || !screenshots.length) return null;
  const latest = screenshots.reduce((currentLatest, shot) => {
    if (!shot || !shot.url) return currentLatest;
    if (!currentLatest) return shot;
    return Number(shot.ts || 0) > Number(currentLatest.ts || 0) ? shot : currentLatest;
  }, null);
  if (!latest?.url) return null;
  return {
    updated_at: new Date().toISOString(),
    source: "field_tablet",
    url: latest.url,
    ts: latest.ts ?? null,
  };
};

export const getScreenshotUrlFromResult = (screenshotResult) => {
  if (!screenshotResult) return "";
  const data = screenshotResult?.data ?? screenshotResult;
  if (typeof data === "string") return data.trim();
  if (data && typeof data === "object") {
    return String(data.screenshot_url || data.image_url || data.url || "").trim();
  }
  return "";
};

export const getNcrStatusPayload = (statusResult) => {
  if (!statusResult) return null;
  if (statusResult?.ncr_status && typeof statusResult.ncr_status === "object") {
    return statusResult.ncr_status;
  }
  const payload = statusResult?.data ?? statusResult;
  if (!payload || typeof payload !== "object") return null;
  if (payload?.ncr_status && typeof payload.ncr_status === "object") {
    return payload.ncr_status;
  }
  if (payload?.active_server || payload?.activeServer || payload?.app_timestamp != null) {
    return {
      active_server: payload.active_server || payload.activeServer || null,
      app_timestamp: payload.app_timestamp ?? payload.appTimestamp ?? null,
      message: payload.message || null,
    };
  }
  const nested = payload?.data;
  if (nested && typeof nested === "object") return nested;
  return null;
};

export const extractLocationFromLogData = (logResult) => {
  const payload = getLocationLogPayload(logResult);
  if (!payload) return null;
  const parent = logResult?.data ?? logResult;
  return readLocationCoordinates(payload) || readLocationCoordinates(parent);
};

/** Normalized location/version row for branch-level device selection views. */
export const buildLocationVersionEntryFromDevice = (
  device,
  locationSource = "logs",
  options = {}
) => {
  const deviceId = String(device?.deviceId || "").trim();
  const deviceInfo =
    device?.deviceInfo && typeof device.deviceInfo === "object" ? device.deviceInfo : {};

  if (locationSource === "mdm") {
    const parsedLocation = extractLocationFromDeviceInfo(deviceInfo);
    const appVersion =
      deviceInfo?.app_version ??
      deviceInfo?.appVersion ??
      deviceInfo?.fullyPackageVersion ??
      deviceInfo?.packageVersion ??
      null;
    const updatedAt = deviceInfo?.timestamp ?? null;
    return {
      deviceId,
      retailerName: device?.retailerName ?? null,
      branchName: device?.branchName ?? null,
      appVersion,
      updatedAt,
      latitude: parsedLocation?.locationLatitude ?? null,
      longitude: parsedLocation?.locationLongitude ?? null,
      hasData: Boolean(
        parsedLocation ||
          (appVersion != null && appVersion !== "") ||
          updatedAt
      ),
    };
  }

  const cachedLocationLog = readDevicePayloadField(device, ["location_log"]);
  const actionLogResult = options?.locationLogResult ?? null;
  const actionPayload = getLocationLogPayload(actionLogResult);
  const locationLog = actionPayload || cachedLocationLog;
  const logResult = locationLog
    ? {
        status: "ok",
        data: locationLog,
        location_log: locationLog,
        updated_at:
          actionLogResult?.updated_at ??
          locationLog?.updated_at ??
          locationLog?.updatedAt,
      }
    : null;
  const parsedLocation = extractLocationFromLogData(logResult);
  const appVersion = locationLog?.app_version ?? locationLog?.appVersion ?? null;
  const updatedAt =
    actionLogResult?.updated_at ??
    locationLog?.updated_at ??
    locationLog?.updatedAt ??
    null;

  return {
    deviceId,
    retailerName: device?.retailerName ?? null,
    branchName: device?.branchName ?? null,
    appVersion,
    updatedAt,
    latitude: parsedLocation?.locationLatitude ?? null,
    longitude: parsedLocation?.locationLongitude ?? null,
    hasData: Boolean(
      locationLog && (parsedLocation || (appVersion != null && appVersion !== "") || updatedAt)
    ),
  };
};

export const stringifyForTableCell = (value) => {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  const t = typeof value;
  if (t === "string" || t === "number" || t === "boolean") return String(value);
  if (t === "object") {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

/** Display string for optional cache payload columns (table + Excel). */
export const formatOptionalPayloadCacheColumnValue = (device, column) => {
  const raw = readDevicePayloadField(device, column.payloadKeys);
  if (column.valueKind === "boolean") return formatNullableBoolean(raw);
  if (column.valueKind === "number") {
    const n = Number(raw);
    return Number.isFinite(n) ? String(n) : "No Data";
  }
  if (column.valueKind === "battery") return formatBatteryLevelText(raw);
  if (column.valueKind === "timestamp") return formatTime(raw);
  if (column.valueKind === "locationVersionUpdated") {
    if (!raw || typeof raw !== "object") return "No Data";
    const appVersion = raw.app_version ?? raw.appVersion;
    const updatedAt = raw.updated_at ?? raw.updatedAt;
    if ((appVersion == null || appVersion === "") && !updatedAt) return "No Data";
    const formattedUpdated = updatedAt ? formatTime(updatedAt) : "No Data";
    return `App Version: ${appVersion ?? "No Data"}\nUpdated: ${formattedUpdated}`;
  }
  if (column.valueKind === "ncrStatus") {
    if (!raw || typeof raw !== "object") return "Not available";
    const activeServer = String(raw.active_server || raw.activeServer || "")
      .trim()
      .toUpperCase();
    const appTimestamp = raw.app_timestamp ?? raw.appTimestamp;
    if (!activeServer || (activeServer !== "MAIN" && activeServer !== "BACKUP")) {
      return "Not available";
    }
    const formattedTime = appTimestamp ? formatTime(appTimestamp) : "No Data";
    return `${activeServer} ${formattedTime}`;
  }
  if (raw === null || raw === undefined || raw === "") return "No Data";
  return stringifyForTableCell(raw);
};
